/**
 * +field: the level-up celebration (docs/design/zones.md; user request: it must feel EPIC) and its
 * smaller sibling for bounty turn-ins and dungeon clears.
 *
 * Level up: a centre-screen "LEVEL UP!" title that slams in (scale + blur), a shine sweeps across
 * it, golden rays turn behind it, sparks fly out, the new level counts up under it, then the gains
 * list staggers in (+Health, +Power, "New ability: X — added to your bar" with its icon, talent
 * points, places that opened). A soft gold screen flash (when flashes are allowed) and a floating
 * "+Level" over the hero. Multi-level jumps are one bigger celebration ("LEVEL UP! ×3").
 * Small: "BOUNTY COMPLETE" / "DUNGEON CLEARED" with its rewards, no rays or flash.
 *
 * Reduced motion: no slam, rays, sparks or flash; the banner fades in and out.
 * One widget per HUD; worldApp reaches it through `celebrations.current`.
 */
import type { HudUpdate } from '../hud';
import type { HudWidget } from '../widgets';
import { el } from '../dom';
import { iconUrl } from '../icons';
import type { LevelGain } from '../../world/levelUpModel';

export interface LevelUpShow {
  from: number;
  to: number;
  gains: readonly LevelGain[];
  /** Unit to float "+Level" over (the hero). */
  unitId: string | null;
  /** Gold screen flash allowed (graphics setting). */
  flash: boolean;
  /** Extra first line ("Bounty complete: Slime Cull"). */
  lead?: string;
  /** +specs: the Advancement Trial's version: the big word ("RAVAGER"), the label / value under it, a tint. */
  word?: string;
  label?: string;
  value?: string;
  tint?: string;
}

export interface SmallShow {
  title: string;
  subtitle?: string;
  lines: readonly string[];
}

type Item = { kind: 'level'; show: LevelUpShow } | { kind: 'small'; show: SmallShow };

/** How long each banner stays (plus a little per gain line). */
const LEVEL_MS = 4600;
const LEVEL_LINE_MS = 380;
const SMALL_MS = 2900;
const FLOAT_MS = 2200;

export const celebrations: { current: Celebrations | null } = { current: null };

const reducedMotion = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export class Celebrations implements HudWidget {
  readonly root: HTMLElement;
  private readonly flashEl: HTMLElement;
  private readonly floatEl: HTMLElement;
  private queue: Item[] = [];
  private busyUntil = 0;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private floatUnit: string | null = null;
  private floatUntil = 0;
  private banner: HTMLElement | null = null;

  constructor(hudRoot: HTMLElement) {
    this.root = el('div', 'fx-cel', hudRoot);
    this.flashEl = el('div', 'fx-cel-flash', this.root);
    this.floatEl = el('div', 'fx-cel-float hk-hidden', hudRoot);
    celebrations.current = this;
  }

  levelUp(show: LevelUpShow): void {
    // A level-up replaces a waiting small banner's slot after it, never interrupts a level-up.
    this.queue.push({ kind: 'level', show });
    this.floatUnit = show.unitId;
    this.floatUntil = performance.now() + FLOAT_MS;
    this.floatEl.textContent = show.word ? show.label ?? show.word : show.to - show.from > 1 ? `+${show.to - show.from} Levels` : '+Level';
    this.floatEl.classList.remove('hk-hidden', 'play');
    void this.floatEl.offsetWidth;
    this.floatEl.classList.add('play');
    this.pump();
  }

  small(show: SmallShow): void {
    this.queue.push({ kind: 'small', show });
    this.pump();
  }

  update(h: HudUpdate, now: number): void {
    if (this.queue.length && now >= this.busyUntil) this.pump();
    if (this.floatUnit && now < this.floatUntil) {
      const u = h.unitsById.get(this.floatUnit);
      if (u) {
        const sp = h.renderer.project(u.pos, h.renderer.unitHeight(u.id) + 1.1);
        this.floatEl.style.transform = `translate3d(${Math.round(sp.x)}px,${Math.round(sp.y)}px,0)`;
      }
    } else if (this.floatUnit) {
      this.floatUnit = null;
      this.floatEl.classList.add('hk-hidden');
    }
  }

  private pump(): void {
    const now = performance.now();
    if (now < this.busyUntil) return;
    const item = this.queue.shift();
    if (!item) return;
    this.clearBanner();
    if (item.kind === 'level') this.busyUntil = now + this.showLevel(item.show);
    else this.busyUntil = now + this.showSmall(item.show);
  }

  private clearBanner(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
    this.banner?.remove();
    this.banner = null;
    this.root.parentElement?.classList.remove('fx-celebrating');
  }

  private showLevel(s: LevelUpShow): number {
    const reduced = reducedMotion();
    const gained = Math.max(1, s.to - s.from);
    const b = (this.banner = el('div', `fx-lvl${gained > 1 || s.word ? ' big' : ''}${s.word ? ' spec' : ''}${reduced ? ' calm' : ''}`, this.root));
    if (s.tint) b.style.setProperty('--spec-tint', s.tint);
    // Nameplates and combat text step back while the banner is up (field.css .fx-celebrating).
    this.root.parentElement?.classList.add('fx-celebrating');
    if (!reduced) {
      el('div', 'fx-lvl-rays', b);
      el('div', 'fx-lvl-ring', b);
      const sparks = el('div', 'fx-lvl-sparks', b);
      const n = gained > 1 ? 28 : 18;
      for (let i = 0; i < n; i++) {
        const sp = el('i', 'fx-lvl-spark', sparks);
        // Deterministic spread: golden-angle directions, varied reach and delay.
        const ang = (i * 137.5) % 360;
        sp.style.setProperty('--a', `${ang}deg`);
        sp.style.setProperty('--d', `${150 + ((i * 53) % 170)}px`);
        sp.style.setProperty('--t', `${(i % 6) * 40}ms`);
      }
    }
    const title = el('div', 'fx-lvl-title', b);
    el('span', 'fx-lvl-word', title, s.word ?? (gained > 1 ? `LEVEL UP! ×${gained}` : 'LEVEL UP!'));
    const lvl = el('div', 'fx-lvl-level', b);
    el('span', 'fx-lvl-label', lvl, s.label ?? 'Level');
    const num = el('span', 'fx-lvl-num hk-num', lvl, s.value ?? String(gained > 1 ? s.from : s.to));
    const list = el('div', 'fx-lvl-gains', b);
    const lines: { kind: string; text: string; abilityId?: string }[] = [];
    if (s.lead) lines.push({ kind: 'lead', text: s.lead });
    lines.push(...s.gains);
    lines.forEach((g, i) => {
      const row = el('div', `fx-lvl-gain kind-${g.kind}`, list);
      row.style.setProperty('--i', String(i));
      if (g.abilityId) {
        const url = iconUrl(g.abilityId, 64);
        if (url) {
          const img = el('img', 'fx-lvl-icon', row);
          img.src = url;
          img.alt = '';
        }
      }
      el('span', 'fx-lvl-gain-text', row, g.text);
    });
    if (s.flash && !reduced) {
      this.flashEl.classList.remove('play');
      void this.flashEl.offsetWidth;
      this.flashEl.classList.add('play');
    }
    // Count the level up for a multi-level jump.
    if (gained > 1 && !s.word) {
      for (let k = 1; k <= gained; k++) this.timers.push(setTimeout(() => (num.textContent = String(s.from + k)), 420 + k * 140));
    }
    const ms = LEVEL_MS + lines.length * LEVEL_LINE_MS + (gained > 1 ? 600 : 0);
    this.timers.push(setTimeout(() => b.classList.add('out'), ms - 600));
    this.timers.push(setTimeout(() => this.clearBanner(), ms));
    return ms;
  }

  private showSmall(s: SmallShow): number {
    const reduced = reducedMotion();
    const b = (this.banner = el('div', `fx-small${reduced ? ' calm' : ''}`, this.root));
    el('div', 'fx-small-title', b, s.title);
    if (s.subtitle) el('div', 'fx-small-sub', b, s.subtitle);
    const list = el('div', 'fx-small-lines', b);
    s.lines.forEach((t, i) => {
      const r = el('div', 'fx-small-line', list, t);
      r.style.setProperty('--i', String(i));
    });
    const ms = SMALL_MS + s.lines.length * 250;
    this.timers.push(setTimeout(() => b.classList.add('out'), ms - 500));
    this.timers.push(setTimeout(() => this.clearBanner(), ms));
    return ms;
  }

  dispose(): void {
    this.clearBanner();
    if (celebrations.current === this) celebrations.current = null;
    this.floatEl.remove();
    this.root.remove();
  }
}
